import React from 'react';
import { Card, Skeleton, Empty, Typography } from 'antd';
import { useCurrency } from '../../hooks/useCurrency';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { formatDateOnly } from '../../utils/dateHelpers';
import {
  DailySalesReportDto,
  WeeklySalesReportDto,
  MonthlySalesReportDto,
  HourlySalesBreakdownDto,
  DailySalesBreakdownDto,
  SalesProductBreakdownDto,
  PaymentMethodBreakdownDto,
} from '../../types/reports';

const { Text } = Typography;

interface ReportSummaryProps {
  report?: any;
  loading: boolean;
  period: string;
}

const DAY_LABELS: Record<string, string> = {
  Monday: 'lun', Tuesday: 'mar', Wednesday: 'mié', Thursday: 'jue',
  Friday: 'vie', Saturday: 'sáb', Sunday: 'dom',
};

/**
 * The merged "Resumen" tab: sales chart (hourly for today, daily for
 * week/month), payment methods and top products as proportion rows —
 * every number next to its visualization. Replaces the old separate
 * "Reporte Detallado" and "Gráficos" tabs, which showed the same
 * dataset twice.
 */
const ReportSummary: React.FC<ReportSummaryProps> = ({ report, loading, period }) => {
  const { currencySymbol } = useCurrency();
  const { getLocalDate } = useRestaurantTimeZone();

  if (loading) {
    return <Card><Skeleton active paragraph={{ rows: 8 }} /></Card>;
  }

  if (!report) {
    return <Card><Empty description="No hay datos disponibles para este período" /></Card>;
  }

  const money = (n: number, decimals = 0) =>
    `${currencySymbol}${n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

  const hourlyData: HourlySalesBreakdownDto[] = (report as DailySalesReportDto)?.hourlyBreakdown || [];
  const dailyData: DailySalesBreakdownDto[] =
    (report as WeeklySalesReportDto)?.dailyBreakdown ||
    (report as MonthlySalesReportDto)?.dailyBreakdown ||
    [];
  const topProducts: SalesProductBreakdownDto[] = (report?.topProducts || []).slice(0, 6);
  const paymentMethods: PaymentMethodBreakdownDto[] = report?.paymentMethods || [];

  const totalPayments = paymentMethods.reduce((sum, m) => sum + m.amount, 0);
  const maxProductRevenue = Math.max(...topProducts.map(p => p.revenue), 1);

  // ----- SVG bar chart (one hue for magnitude; today/current dimmed) -----
  // Comensales solo cuando hubo mesas: una hora de puro para llevar no tiene
  const guestsText = (n?: number) => (n ? ` · ${n} comensal${n !== 1 ? 'es' : ''}` : '');
  const isToday = period === 'today';
  const today = getLocalDate();
  const series = isToday
    ? hourlyData.map(h => ({
        key: String(h.hour),
        label: h.hour.toString().padStart(2, '0'),
        peakLabel: h.hour.toString().padStart(2, '0'),
        value: h.revenue,
        tooltip: `${h.timeRange || `${h.hour}:00`} · ${money(h.revenue)} · ${h.orderCount} órdenes${guestsText(h.guestCount)}`,
        isCurrent: false,
      }))
    : dailyData.map(d => ({
        key: d.date,
        label: dailyData.length > 10
          ? formatDateOnly(d.date, 'DD')
          : (DAY_LABELS[d.dayOfWeek] || formatDateOnly(d.date, 'DD/MM')),
        // Bar labels repeat across months or weeks; the peak has to name one day
        peakLabel: formatDateOnly(d.date, 'DD/MM'),
        value: d.revenue,
        tooltip: `${formatDateOnly(d.date, 'DD/MM')} · ${money(d.revenue)} · ${d.orderCount} órdenes${guestsText(d.guestCount)}`,
        // The in-progress day is dimmed so it doesn't read as a drop
        isCurrent: formatDateOnly(d.date, 'YYYY-MM-DD') === today,
      }));

  const maxValue = Math.max(...series.map(s => s.value), 1);
  const peak = series.reduce(
    (best, s) => (s.value > best.value ? s : best),
    { key: '', label: '', peakLabel: '', value: -1, tooltip: '', isCurrent: false }
  );

  // Chart geometry
  const W = 720, H = 170, PAD_L = 44, PAD_R = 10, TOP = 18, BASE = 142;
  const plotW = W - PAD_L - PAD_R;
  const n = Math.max(series.length, 1);
  const step = plotW / n;
  const barW = Math.min(42, Math.max(8, step * 0.55));
  const yFor = (v: number) => BASE - (v / maxValue) * (BASE - TOP);
  // Label every bar when few; thin out for hourly/monthly series
  const labelEvery = n > 20 ? 4 : n > 10 ? 2 : 1;
  const midValue = maxValue / 2;
  const axisFmt = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `${Math.round(v)}`);

  return (
    <div className="rep-grid2">
      {/* Sales chart */}
      <div className="rep-panel rep-wide">
        <div className="rep-panel__head">
          <span className="rep-panel__title">
            {isToday ? 'Ventas por hora' : 'Ventas por día'}
          </span>
          {peak.value > 0 && (
            <span className="rep-panel__meta">
              pico: {peak.peakLabel} · {money(peak.value)}
            </span>
          )}
        </div>
        {series.length === 0 ? (
          <Empty description="Sin ventas en el período" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <svg
            className="rep-chart-svg"
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={isToday ? 'Ventas por hora' : 'Ventas por día'}
          >
            {/* recessive grid + y labels */}
            <line className="gridline" x1={PAD_L} y1={TOP} x2={W - PAD_R} y2={TOP} />
            <line className="gridline" x1={PAD_L} y1={yFor(midValue)} x2={W - PAD_R} y2={yFor(midValue)} />
            <text className="ax" x={PAD_L - 6} y={TOP + 4} textAnchor="end">{axisFmt(maxValue)}</text>
            <text className="ax" x={PAD_L - 6} y={yFor(midValue) + 4} textAnchor="end">{axisFmt(midValue)}</text>
            <text className="ax" x={PAD_L - 6} y={BASE + 4} textAnchor="end">0</text>

            {series.map((s, i) => {
              const x = PAD_L + i * step + (step - barW) / 2;
              const y = yFor(s.value);
              return (
                <g key={s.key}>
                  <rect
                    className={`bar${s.isCurrent ? ' bar--current' : ''}`}
                    x={x}
                    y={y}
                    width={barW}
                    height={Math.max(BASE - y, s.value > 0 ? 2 : 0)}
                    rx={3}
                  >
                    <title>{s.tooltip}</title>
                  </rect>
                  {i % labelEvery === 0 && (
                    <text className="ax" x={x + barW / 2} y={BASE + 14} textAnchor="middle">
                      {s.label}{s.isCurrent ? '*' : ''}
                    </text>
                  )}
                </g>
              );
            })}

            {/* selective direct label: the peak only */}
            {peak.value > 0 && (
              <text
                className="dlabel"
                x={PAD_L + series.indexOf(peak) * step + step / 2}
                y={yFor(peak.value) - 5}
                textAnchor="middle"
              >
                {money(peak.value)}
              </text>
            )}
            <line className="baseline" x1={PAD_L} y1={BASE} x2={W - PAD_R} y2={BASE} />
          </svg>
        )}
        {series.some(s => s.isCurrent) && (
          <Text type="secondary" style={{ fontSize: 11 }}>* día en curso</Text>
        )}
      </div>

      {/* Payment methods */}
      <div className="rep-panel">
        <div className="rep-panel__head">
          <span className="rep-panel__title">Ventas por método</span>
          {totalPayments > 0 && (
            <span className="rep-panel__meta">{money(totalPayments, 2)}</span>
          )}
        </div>
        {paymentMethods.length === 0 ? (
          <Empty description="Sin pagos en el período" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          paymentMethods.map((m: any) => (
            <div key={m.methodId || m.displayName} className="rep-hrow">
              <span className="rep-hrow__label">{m.displayName}</span>
              <span className="rep-hrow__bar">
                <i style={{ width: totalPayments > 0 ? `${(m.amount / totalPayments) * 100}%` : 0 }} />
              </span>
              <span className="rep-hrow__amount">
                {money(m.amount, 2)} <small>{(m.percentage ?? 0).toFixed(0)}% · {m.count}</small>
              </span>
            </div>
          ))
        )}
      </div>

      {/* Top products */}
      <div className="rep-panel">
        <div className="rep-panel__head">
          <span className="rep-panel__title">Top productos</span>
          <span className="rep-panel__meta">detalle en la pestaña Productos</span>
        </div>
        {topProducts.length === 0 ? (
          <Empty description="Sin productos vendidos" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          topProducts.map((p: any) => (
            <div key={p.productId || p.productName} className="rep-hrow">
              <span className="rep-hrow__label">{p.productName}</span>
              <span className="rep-hrow__bar">
                <i style={{ width: `${(p.revenue / maxProductRevenue) * 100}%` }} />
              </span>
              <span className="rep-hrow__amount">
                {money(p.revenue, 2)} <small>×{p.quantitySold}</small>
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ReportSummary;
